<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $tc('messages.programmings', 2),
                        href: meta.links.index,
                    },
                    {
                        name: programming.title,
                        href: null,
                    },
                ]"
            />
        </template>

        <div class="w-full flex gap-x-2 justify-end">
            <AppButton :href="programming.links.edit" v-if="meta.can.edit">
                {{ $t('messages.edit') }}
            </AppButton>
            <AppButton element="a" :href="meta.links.print" target="_blank">
                <IconPrinter class="-ml-0.5 mr-2 w-4 h-4 text-gray-700" />
                {{ $t('messages.print') }}
            </AppButton>
        </div>

        <TitleFieldsFormSection>
            <template v-slot:title>
                {{ $t('messages.information') }}
            </template>
            <div class="md:grid md:grid-cols-6 md:gap-6">
                <div class="sm:col-span-2">
                    <label class="block text-sm font-medium leading-5 text-gray-700">
                        {{ $t('messages.trimester') }}
                    </label>
                    <div class="mt-1">
                        {{ programming.trimester }}
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <label class="block text-sm font-medium leading-5 text-gray-700">
                        {{ $t('messages.grade_level') }}
                    </label>
                    <div class="mt-1">
                        {{ programming.grade_level_names }}
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <label class="block text-sm font-medium leading-5 text-gray-700">
                        {{ $t('messages.groups') }}
                    </label>
                    <div class="mt-1">
                        <span v-if="programming.groups.length === 0">-</span>
                        <BadgePill v-for="group in programming.groups" :key="group.id">
                            {{ group.name }}
                        </BadgePill>
                    </div>
                </div>
            </div>
        </TitleFieldsFormSection>

        <details class="mt-3">
            <summary>
                <h2 class="inline-block text-lg leading-6 font-medium text-gray-900 mb-3">
                    {{ $t('messages.details') }}
                </h2>
            </summary>

            <TitleFieldsFormSection v-if="programming.short_description">
                <template v-slot:title>
                    {{ $t('messages.short_description') }}
                </template>

                <p v-html="programming.short_description" />
            </TitleFieldsFormSection>

            <TitleFieldsFormSection v-if="programming.methodology">
                <template v-slot:title>
                    {{ $t('messages.methodology') }}
                </template>

                <p v-html="programming.methodology" />
            </TitleFieldsFormSection>

            <TitleFieldsFormSection v-if="programming.diversity_observations">
                <template v-slot:title>
                    {{ $t('messages.diversity_observations') }}
                </template>

                <p v-html="programming.diversity_observations" />
            </TitleFieldsFormSection>

            <TitleFieldsFormSection v-if="programming.learning_aims">
                <template v-slot:title>
                    {{ $t('messages.learning_aims') }}
                </template>

                <p v-html="programming.learning_aims" />
            </TitleFieldsFormSection>

            <TitleFieldsFormSection v-if="programming.general_contents">
                <template v-slot:title>
                    {{ $t('messages.general_contents') }}
                </template>

                <p v-html="programming.general_contents" />
            </TitleFieldsFormSection>

            <TitleFieldsFormSection v-if="programming.general_competences">
                <template v-slot:title>
                    {{ $t('messages.general_competences') }}
                </template>

                <p v-html="programming.general_competences" />
            </TitleFieldsFormSection>

            <TitleFieldsFormSection v-if="programming.learning_vectors">
                <template v-slot:title>
                    {{ $t('messages.learning_vectors') }}
                </template>

                <p v-html="programming.learning_vectors" />
            </TitleFieldsFormSection>

            <TitleFieldsFormSection v-if="programming.evaluation_criteria">
                <template v-slot:title>
                    {{ $t('messages.evaluation_criteria') }}
                </template>

                <p v-html="programming.evaluation_criteria" />
            </TitleFieldsFormSection>
        </details>

        <details class="mt-3">
            <summary>
                <h2 class="inline-block text-lg leading-6 font-medium text-gray-900 mb-3">
                    {{ $tc('messages.special_adaptations', 2) }} ({{ programming.specialAdaptations.length }})
                </h2>
            </summary>

            <ul>
                <li v-for="student in programming.specialAdaptations" class="mt-3" :key="student.id">
                    <div>
                        <StudentInfoItem :student="student" />
                    </div>
                    <p class="bg-gray-100 p-1" v-html="student.special_adaptation" />
                </li>
            </ul>
        </details>

        <div v-if="programming.syllabusSummary.count === 0">
            <EmptyState :title="$t('messages.there_are_no_activities_with_evaluation_values')" with-icon />
        </div>
        <!--        <SyllabusSummary :summary="programming.syllabusSummary" />-->

        <details class="mt-3" v-if="syllabusSummary.length > 0">
            <summary>
                <h2 class="inline-block text-lg leading-6 font-medium text-gray-900 mb-3">
                    {{ $tc('messages.evaluation_criteria', 1) }}
                </h2>
            </summary>

            <div class="flex flex-col mt-5">
                <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                    <div
                        class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                    >
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead>
                                <tr>
                                    <th
                                        class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $tc('messages.evaluation_criteria', 1) }}
                                    </th>
                                    <th
                                        class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $tc('messages.dimensions', 2) }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                    v-for="(evaluationCriterion, index) in syllabusSummary"
                                    :key="evaluationCriterion.id"
                                >
                                    <td
                                        class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                    >
                                        {{ evaluationCriterion.title }}
                                    </td>
                                    <td
                                        class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                    >
                                        <div v-for="dimension in evaluationCriterion.dimensions" :key="dimension.id">
                                            {{ dimension.abbreviation }} -
                                            {{ dimension.title }}
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </details>

        <div class="mt-5">
            <h2 class="text-lg leading-6 font-medium text-gray-900 mb-3">
                {{ $tc('messages.activities', 2) }}
            </h2>

            <Tabs :default-index="selectedSubject" @tab-changed="(newIndex) => (selectedSubject = newIndex)">
                <Tab
                    :name="programming_subject.subject.name"
                    :title="programming_subject.subject.name"
                    v-for="programming_subject in programmingData.programming_subjects"
                    :key="programming_subject.subject.name"
                >
                    <div class="flex flex-col mt-5">
                        <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                            <div
                                class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                            >
                                <table class="table-fixed min-w-full divide-y divide-gray-200">
                                    <thead>
                                        <tr>
                                            <th
                                                class="w-10 max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                            >
                                                {{ $tc('messages.sessions', 1) }}
                                            </th>
                                            <th
                                                class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                            >
                                                {{ $tc('messages.activities', 2) }}
                                            </th>
                                        </tr>
                                    </thead>
                                    <!--                                    :list="programming_subject.sessions"-->
                                    <draggable
                                        tag="tbody"
                                        v-model="programming_subject.sessions"
                                        group="people"
                                        @start="drag = true"
                                        @end="drag = false"
                                        handle=".handle"
                                        @change="reorderSessions(programming_subject)"
                                        item-key="id"
                                    >
                                        <template #item="{ element: session, index }">
                                            <tr :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-100'">
                                                <td
                                                    class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                                >
                                                    <div class="flex flex-col justify-start items-center">
                                                        <div class="w-6 h-6 flex justify-center align-middle">
                                                            {{ session.order }}
                                                        </div>
                                                        <div v-if="meta.can.edit">
                                                            <AddSessionActivitiesForm
                                                                :programming="programming"
                                                                :programming_subject="programming_subject"
                                                                :session="session"
                                                                @programming-updated="updateProgramming()"
                                                            >
                                                                <svg
                                                                    class="w-6 h-6 text-gray-700"
                                                                    fill="currentColor"
                                                                    viewBox="0 0 20 20"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                >
                                                                    <path
                                                                        fill-rule="evenodd"
                                                                        d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                                                                        clip-rule="evenodd"
                                                                    ></path>
                                                                </svg>
                                                            </AddSessionActivitiesForm>
                                                        </div>
                                                        <div v-if="meta.can.edit">
                                                            <AddSessionGroupsForm
                                                                :session="session"
                                                                :groups="programming.groups"
                                                                @programming-updated="updateProgramming()"
                                                            >
                                                                <svg
                                                                    class="w-6 h-6"
                                                                    :class="
                                                                        session.groups_count
                                                                            ? 'text-indigo-700'
                                                                            : 'text-gray-700'
                                                                    "
                                                                    fill="currentColor"
                                                                    viewBox="0 0 20 20"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                >
                                                                    <path
                                                                        fill-rule="evenodd"
                                                                        d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                                                                        clip-rule="evenodd"
                                                                    ></path>
                                                                </svg>
                                                            </AddSessionGroupsForm>
                                                        </div>
                                                        <div>
                                                            <div v-for="group in session.groups" :key="group.name">
                                                                <span v-if="group.date" class="text-xs">
                                                                    {{ group.name }}
                                                                    {{ group.display_info }}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div v-if="session.activities.length === 0">
                                                            <DeleteSessionButton
                                                                :url="
                                                                    route('sessions.destroy', { session: session.id })
                                                                "
                                                                @programming-updated="updateProgramming()"
                                                                v-if="meta.can.edit"
                                                            />
                                                        </div>
                                                        <div>
                                                            <IconGripVertical
                                                                class="handle inline-block h-4 w-4 text-gray-600 cursor-move"
                                                            />
                                                        </div>
                                                    </div>
                                                </td>
                                                <td
                                                    class="truncate px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-900"
                                                >
                                                    <div v-if="session.activities.length === 0">
                                                        <span class="text-gray-400">
                                                            {{ $t('messages.without_activities') }}
                                                        </span>
                                                    </div>

                                                    <div
                                                        v-for="(activity, activityIndex) in session.activities"
                                                        class=""
                                                        v-else
                                                    >
                                                        <div
                                                            class="flex space-x-2"
                                                            :class="{
                                                                'mt-5': activityIndex !== 0,
                                                            }"
                                                        >
                                                            <div class="flex flex-col space-y-1">
                                                                <a
                                                                    :href="route('activities.show', activity.id)"
                                                                    class="inline-flex items-center px-1 py-1 border border-transparent text-xs font-medium rounded text-gray-700 bg-gray-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                                                >
                                                                    <svg
                                                                        viewBox="0 0 20 20"
                                                                        fill="currentColor"
                                                                        class="eye w-3 h-3"
                                                                    >
                                                                        <path
                                                                            d="M10 12a2 2 0 100-4 2 2 0 000 4z"
                                                                        ></path>
                                                                        <path
                                                                            fill-rule="evenodd"
                                                                            d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                                                                            clip-rule="evenodd"
                                                                        ></path>
                                                                    </svg>
                                                                </a>
                                                                <a
                                                                    :href="route('activities.edit', activity.id)"
                                                                    class="inline-flex items-center px-1 py-1 border border-transparent text-xs font-medium rounded text-gray-700 bg-gray-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                                                >
                                                                    <svg
                                                                        viewBox="0 0 20 20"
                                                                        fill="currentColor"
                                                                        class="eye w-3 h-3"
                                                                    >
                                                                        <path
                                                                            d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"
                                                                        ></path>
                                                                    </svg>
                                                                </a>
                                                            </div>
                                                            <div class="w-full">
                                                                <div>
                                                                    <div
                                                                        class="inline-block"
                                                                        :title="$t('messages.to_be_graded')"
                                                                        v-if="activity.weight"
                                                                    >
                                                                        <svg
                                                                            class="w-4 h-4 text-indigo-400"
                                                                            fill="currentColor"
                                                                            viewBox="0 0 20 20"
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                        >
                                                                            <path
                                                                                fill-rule="evenodd"
                                                                                d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                                                clip-rule="evenodd"
                                                                            ></path>
                                                                        </svg>
                                                                    </div>
                                                                    <span class="font-semibold">
                                                                        {{ activity.title }}
                                                                    </span>
                                                                    <span v-if="activity.grouping_display" class="ml-1">
                                                                        ({{ activity.grouping_display }})
                                                                    </span>
                                                                    <span v-if="activity.duration">
                                                                        &nbsp;- {{ activity.duration }}
                                                                        {{ $t('messages.minute_abbreviation') }}
                                                                    </span>
                                                                    <span
                                                                        v-if="activity.attention_to_diversity !== null"
                                                                        :title="$t('messages.attention_to_diversity')"
                                                                    >
                                                                        <svg
                                                                            class="inline-block w-4 h-4 text-indigo-600"
                                                                            fill="currentColor"
                                                                            viewBox="0 0 20 20"
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                        >
                                                                            <path
                                                                                fill-rule="evenodd"
                                                                                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-2 0c0 .993-.241 1.929-.668 2.754l-1.524-1.525a3.997 3.997 0 00.078-2.183l1.562-1.562C15.802 8.249 16 9.1 16 10zm-5.165 3.913l1.58 1.58A5.98 5.98 0 0110 16a5.976 5.976 0 01-2.516-.552l1.562-1.562a4.006 4.006 0 001.789.027zm-4.677-2.796a4.002 4.002 0 01-.041-2.08l-.08.08-1.53-1.533A5.98 5.98 0 004 10c0 .954.223 1.856.619 2.657l1.54-1.54zm1.088-6.45A5.974 5.974 0 0110 4c.954 0 1.856.223 2.657.619l-1.54 1.54a4.002 4.002 0 00-2.346.033L7.246 4.668zM12 10a2 2 0 11-4 0 2 2 0 014 0z"
                                                                                clip-rule="evenodd"
                                                                            ></path>
                                                                        </svg>
                                                                    </span>
                                                                </div>

                                                                <div class="md:grid md:grid-cols-3 md:gap-6">
                                                                    <p
                                                                        class="max-w-2xl break-words whitespace-normal sm:col-span-2"
                                                                    >
                                                                        {{ activity.description }}
                                                                    </p>
                                                                    <div>
                                                                        <div v-for="material in activity.materials">
                                                                            <span v-if="material.url === null">
                                                                                {{ material.title }}
                                                                            </span>
                                                                            <a
                                                                                v-else
                                                                                :href="material.url"
                                                                                v-text="material.title"
                                                                                class="text-indigo-500"
                                                                            ></a>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        </template>
                                    </draggable>
                                    <tfoot v-if="meta.can.edit">
                                        <tr
                                            :class="
                                                programming_subject.sessions.length % 2 === 0
                                                    ? 'bg-white'
                                                    : 'bg-gray-50'
                                            "
                                        >
                                            <td class="" colspan="2">
                                                <div class="flex justify-center mt-3 mb-1">
                                                    <AddSubjectSessionsForm
                                                        :programming_subject="programming_subject"
                                                        @programming-updated="updateProgramming()"
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    </div>
                </Tab>
            </Tabs>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import TitleFieldsFormSection from '@/components/TitleFieldsFormSection.vue';
import Tab from '@/components/Tab.vue';
import Tabs from '@/components/Tabs.vue';
import BadgePill from '@/components/BadgePill.vue';
import AddSubjectSessionsForm from '@/components/pageSpecific/programming/AddSubjectSessionsForm.vue';
import AddSessionActivitiesForm from '@/components/pageSpecific/programming/AddSessionActivitiesForm.vue';
import AddSessionGroupsForm from '@/components/pageSpecific/programming/AddSessionGroupsForm.vue';
import EmptyState from '@/components/EmptyState.vue';
import draggable from 'vuedraggable';
import DeleteSessionButton from '@/components/pageSpecific/programming/DeleteSessionButton.vue';
import AppButton from '@/components/AppButton.vue';
import { IconGripVertical, IconPrinter } from '@tabler/icons-vue';
import StudentInfoItem from '@/components/StudentInfoItem.vue';

export default {
    components: {
        StudentInfoItem,
        AppButton,
        AddSessionActivitiesForm,
        DeleteSessionButton,
        AddSubjectSessionsForm,
        PanelPage,
        TitleFieldsFormSection,
        BadgePill,
        Tab,
        Tabs,
        AddSessionGroupsForm,
        EmptyState,
        draggable,
        IconPrinter,
        IconGripVertical,
    },
    props: ['meta', 'programming', 'syllabusSummary'],
    layout: Layout,
    data() {
        return {
            showAddSessions: null,
            programmingData: JSON.parse(JSON.stringify(this.programming)),
            selectedSubject: 0,
        };
    },
    name: 'Show',
    computed: {},
    watch: {
        programming: {
            handler() {
                // this.updateProgramming();
            },
            deep: true,
        },
    },
    methods: {
        updateProgramming() {
            this.programmingData = JSON.parse(JSON.stringify(this.programming));
            this.$inertia.visit(this.meta.links.show, {
                preserveState: false,
                preserveScroll: true,
            });
        },
        reorderSessions(programming_subject) {
            const newOrder = programming_subject.sessions.map((s) => s.id);

            this.$inertia.patch(
                route('reorder_session.update'),
                { sessions: newOrder },
                {
                    preserveScroll: true,
                },
            );
        },
    },
    mounted() {
        this.selectedSubject = this.programming.selectedSubjectIndex;
    },
};
</script>
